/* global setTimeout clearTimeout $ window console document */
import * as React from "react";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector, useStore } from "react-redux";
import { select, normalize } from "../../state/selectionSlice";
import { cancelDrag, startDrag } from "../../helpers/dragAndDrop";
import { move } from "../../state/structureSlice";
import { findIndex, getPinsForMoving, getWeights } from "../../helpers/grid";
import { runAutofit } from "../cells/cellAutofitUtils";

const HeaderHandle = ({ vertical }) => {
  if (vertical) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <svg width="4" height="8" viewBox="0 0 4 8" fill="none" style={{ flexShrink: 0, marginRight: 2 }}>
          <path d="M0 4L4 8V0L0 4Z" fill="white" />
        </svg>
        <div style={{ background: "white", width: 2, height: 8, borderRadius: 1, flexShrink: 0 }} />
        <div style={{ background: "white", width: 2, height: 8, borderRadius: 1, flexShrink: 0, marginLeft: 1 }} />
        <div style={{ background: "white", width: 2, height: 8, borderRadius: 1, flexShrink: 0, marginLeft: 1 }} />
        <svg width="4" height="8" viewBox="0 0 4 8" fill="none" style={{ flexShrink: 0, marginLeft: 2 }}>
          <path d="M4 4L0 0V8L4 4Z" fill="white" />
        </svg>
      </div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
      }}
    >
      <svg width="8" height="4" viewBox="0 0 8 4" fill="none" style={{ flexShrink: 0, marginBottom: 2 }}>
        <path d="M4 0L8 4H0L4 0Z" fill="white" />
      </svg>
      <div style={{ background: "white", width: 8, height: 2, borderRadius: 1, flexShrink: 0 }} />
      <div style={{ background: "white", width: 8, height: 2, borderRadius: 1, flexShrink: 0, marginTop: 1 }} />
      <div style={{ background: "white", width: 8, height: 2, borderRadius: 1, flexShrink: 0, marginTop: 1 }} />
      <svg width="8" height="4" viewBox="0 0 8 4" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
        <path d="M4 4L0 0H8L4 4Z" fill="white" />
      </svg>
    </div>
  );
};

HeaderHandle.propTypes = {
  vertical: PropTypes.bool.isRequired,
};

const Header = (props) => {
  const [onHovered, setOnHovered] = useState(false);
  const [onAreaHovered, setOnAreaHovered] = useState(false);
  const [mouseOverDocument, setMouseOverDocument] = useState(false);
  const dispatch = useDispatch();
  const cols = useSelector((state) => state.gridd.present.structure.columns);
  const rows = useSelector((state) => state.gridd.present.structure.rows);
  const tableSelected = useSelector((state) => state.selection.table);
  const isSelected = useSelector((state) =>
    props.vertical
      ? state.selection.columns?.findIndex((c) => c === props.index) !== -1
      : state.selection.rows?.findIndex((r) => r === props.index) !== -1
  );
  const cells = useSelector((state) => state.gridd.present.structure.cells);
  const merged = useSelector((state) => state.gridd.present.structure.merged);
  const store = useStore();

  function clearDragTimeout() {
    if (window.draggedObject) {
      if (timeout) {
        clearTimeout(timeout);
        setTimeoutHandle(null);
        cancelDrag();
        console.log("Cancelled drag fully");
      } else {
        cancelDrag();
        console.log("No timeout handle found to clear drag timeout, cancelled drag anyway");
      }

      if (window.draggedObject?.overlayLine) {
        window.draggedObject.overlayLine.remove();
      }
    }
  }

  function startDragOnGrabTimeout(e) {
    const location = $(":hover").last().offset();
    console.log("Mouse location after timeout", {
      e,
      location,
      onHovered,
      onAreaHovered,
      mouseOverDocument,
      props,
    });
    if (location.left === 0 && location.top === 0) return; // cancelling drag start if mouse seems out of document
    // start drag with a minimal empty preview, we'll use the previewElement for the blue line
    dispatch(
      select({
        type: "clear",
        column: props.vertical ? props.index : undefined,
        row: !props.vertical ? props.index : undefined,
      })
    );
    startDrag(e, {
      content: {
        column: props.vertical ? props.index : undefined,
        row: !props.vertical ? props.index : undefined,
      },
      weights: getWeights(props.vertical ? cols : rows),
      previewContent: $("<div></div>"),
      onDragEnd,
      onDragMove,
    });
    // style the autogenerated previewElement as our blue drop-target line
    const previewEl = $("#div_preview");
    previewEl.css({
      position: "absolute",
      left: 28,
      top: 28,
      pointerEvents: "none",
      zIndex: 2000,
      backgroundColor: "transparent",
      boxShadow: "0px 0px 8px 2px rgba(0, 101, 253, 0.5)",
      border: "3px dashed #0065FD",
      boxSizing: "border-box",
    });
    if (props.vertical) {
      const pins = getPinsForMoving(cols, window.innerWidth);
      const colWidth = pins[props.index + 1] - pins[props.index];
      previewEl.css({
        width: `${colWidth}px`,
        height: "calc(100vh - 56px)",
        backgroundColor: "rgba(0, 101, 253, 0.3)",
        transform: `translate(${pins[props.index]}px, 0px)`,
      });
    } else {
      const pins = getPinsForMoving(rows, window.innerHeight);
      const rowHeight = pins[props.index + 1] - pins[props.index];
      previewEl.css({
        height: `${rowHeight}px`,
        width: "calc(100vw - 56px)",
        backgroundColor: "rgba(0, 101, 253, 0.3)",
        transform: `translate(0px, ${pins[props.index]}px)`,
      });
    }
    // after the initial styles are set, add the transition to animate subsequent moves
    setTimeout(() => {
      previewEl.css({
        transition: "transform 0.15s ease-out, width 0.15s ease-out, height 0.15s ease-out",
      });
    }, 0);
  }

  let [timeout, setTimeoutHandle] = React.useState(null);
  let targetPin = null;

  function onDragEnd() {
    // remove overlay line
    if (window.draggedObject?.overlayLine) {
      window.draggedObject.overlayLine.remove();
    }
    if (targetPin && (targetPin.index < props.index || targetPin.index > props.index + 1)) {
      let toIdx = targetPin.index;
      // when dragging forward (from a lower index to a higher pin) the removal of the element
      // shifts the destination index by -1 – compensate for it here
      if (props.index < toIdx) {
        toIdx = toIdx - 1;
      }
      dispatch(move({ vertical: props.vertical, from: props.index, to: toIdx }));
      dispatch(
        select({
          column: props.vertical ? Math.max(0, toIdx) : undefined,
          row: !props.vertical ? Math.max(0, toIdx) : undefined,
          type: "clear",
        })
      );
    }
  }

  function onDragMove(event, obj) {
    // position the blue line by moving the previewElement
    const { column, row } = obj.content;
    if (column !== undefined) {
      const pins = getPinsForMoving(cols, window.innerWidth);
      const idx = Math.max(0, Math.min(cols.length - 1, findIndex(pins, event.clientX - 28)));
      const targetIdx = props.index < idx ? idx + 1 : idx;
      targetPin = { pinX: pins[targetIdx] ?? 0, index: targetIdx };

      const colWidth = pins[idx + 1] - pins[idx];
      obj.previewElement.css({ transform: `translate(${pins[idx]}px, 0px)`, width: `${colWidth}px` });
    } else if (row !== undefined) {
      const pins = getPinsForMoving(rows, window.innerHeight);
      const idx = Math.max(0, Math.min(rows.length - 1, findIndex(pins, event.clientY - 28)));
      const targetIdx = props.index < idx ? idx + 1 : idx;
      targetPin = { pinY: pins[targetIdx] ?? 0, index: targetIdx };

      const rowHeight = pins[idx + 1] - pins[idx];
      obj.previewElement.css({ transform: `translate(0px, ${pins[idx]}px)`, height: `${rowHeight}px` });
    }
  }

  function onDoubleClick() {
    document.activeElement?.blur();
    setTimeout(() => {
      const cellOverflow = store.getState().app.cellOverflow;
      runAutofit({
        vertical: props.vertical,
        index: props.index,
        cols,
        rows,
        cells,
        merged,
        cellOverflow,
        dispatch,
        preferredNeighborDirection: +1,
      });
    }, 0);
  }

  useEffect(() => {
    function handleMouseMove(e) {
      setMouseOverDocument(true);
      if (props.vertical) {
        const correctedX = e.clientX - 28;
        const pins = getPinsForMoving(cols, window.innerWidth);
        if (correctedX > pins[props.index] + 5 && correctedX < pins[props.index + 1] - 5) {
          setOnAreaHovered(true);
        } else setOnAreaHovered(false);
      } else {
        const correctedY = e.clientY - 28;
        const pins = getPinsForMoving(rows, window.innerHeight);
        if (correctedY > pins[props.index] + 5 && correctedY < pins[props.index + 1] - 5) {
          setOnAreaHovered(true);
        } else setOnAreaHovered(false);
      }
    }
    function handleMouseLeave() {
      setMouseOverDocument(false);
      cancelDrag();
    }
    $("body").on("mousemove", handleMouseMove);
    $("body").on("mouseleave", handleMouseLeave);
    return () => {
      $("body").off("mousemove", handleMouseMove);
      $("body").off("mouseleave", handleMouseLeave);
    };
  });

  function getBackground() {
    if (!mouseOverDocument) {
      return "transparent";
    }
    if (isSelected) {
      return "#0065FD";
    }
    if (!onAreaHovered) {
      return "lightgray";
    }
    return onHovered ? "darkgray" : "gray";
  }

  return tableSelected ? (
    <></>
  ) : isSelected ? (
    <div
      style={{
        position: "relative",
        top: props.vertical ? 9 : undefined,
        height: props.vertical ? "12px" : "100%",
        width: props.vertical ? "100%" : "12px",
        left: props.vertical ? -1 : 7,
        alignContent: "center",
      }}
      onMouseDown={(e) => {
        if (e.button === 0) {
          startDragOnGrabTimeout(e);
          e.preventDefault();
        }
      }}
      onMouseUp={(e) => clearDragTimeout(e)}
      onDoubleClick={onDoubleClick}
    >
      <div
        style={{
          background: getBackground(),
          width: props.vertical ? "90%" : 12,
          height: props.vertical ? 12 : "90%",
          position: "relative",
          marginLeft: props.vertical ? "auto" : undefined,
          marginRight: props.vertical ? "auto" : undefined,
          marginTop: !props.vertical ? "auto" : undefined,
          borderRadius: 6,
          maxWidth: props.vertical ? "50%" : undefined,
          maxHeight: !props.vertical ? "50%" : undefined,
        }}
      >
        <HeaderHandle vertical={props.vertical} />
      </div>
    </div>
  ) : (
    <div
      id={`header_${props.vertical ? "vertical" : "horizontal"}_${props.index}`}
      style={{
        position: "relative",
        top: props.vertical ? 9 : undefined,
        height: props.vertical ? "12px" : "100%",
        width: props.vertical ? "100%" : "12px",
        left: props.vertical ? -1 : 7,
        alignContent: "center",
      }}
      onMouseEnter={() => {
        setOnHovered(true);
      }}
      onMouseLeave={() => {
        setOnHovered(false);
      }}
      onMouseDown={(e) => {
        timeout = setTimeout(() => startDragOnGrabTimeout(e), 300);
        e.preventDefault();
      }}
      onMouseUp={() => {
        if (timeout) {
          clearTimeout(timeout);
        }
      }}
      onClick={(e) => {
        dispatch(
          select({
            column: props.vertical ? props.index : undefined,
            row: !props.vertical ? props.index : undefined,
            type: e.shiftKey ? "box" : e.ctrlKey || e.metaKey ? "additive" : "clear",
          })
        );
        dispatch(normalize({ colCount: cols.length, rowCount: rows.length, isAdditive: e.ctrlKey || e.metaKey }));
      }}
      onDoubleClick={onDoubleClick}
    >
      <div
        style={{
          background: getBackground(),
          width: props.vertical ? "90%" : 12,
          height: props.vertical ? 12 : "90%",
          position: "relative",
          marginLeft: props.vertical ? "auto" : undefined,
          marginRight: props.vertical ? "auto" : undefined,
          marginTop: !props.vertical ? "auto" : undefined,
          borderRadius: 6,
          maxWidth: props.vertical ? "50%" : undefined,
          maxHeight: !props.vertical ? "50%" : undefined,
        }}
      >
        {getBackground() !== "transparent" && <HeaderHandle vertical={props.vertical} />}
      </div>
    </div>
  );
};

Header.propTypes = {
  vertical: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
};

export default Header;
